import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { client } from '../api/client';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { AttendanceDetail } from '../components/admin/AttendanceDetail';
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  Clock,
  Lock,
  MapPin,
  Pin,
  Search,
  Shield,
  Users,
  X,
} from 'lucide-react';

interface OrchestraGroup { id: string; name: string; }

interface GroupAttendanceCount {
  groupId: string;
  name: string;
  count: number;
}

interface MyEvent {
  id: string;
  title: string;
  type: string;
  date: string;
  startTime: string;
  endTime: string | null;
  location: string | null;
  description: string | null;
  color: string | null;
  status: string;
  isPinned: boolean;
  groups: OrchestraGroup[];
  myAttendance: string | null;
  attendanceLocked: boolean;
  canSetAttendance: boolean;
  participantTotal: number;
  filledTotal: number;
  groupCounts: GroupAttendanceCount[];
  groupCountsImported?: boolean;
}

interface Attendee {
  userId: string;
  firstName: string;
  lastName: string;
  instrument: string | null;
  groups: OrchestraGroup[];
  status: string;
}

const STATUS_OPTIONS = [
  { value: 'aanwezig', label: 'Aanwezig', short: 'Ja' },
  { value: 'afwezig', label: 'Afwezig', short: 'Nee' },
  { value: 'onder_voorbehoud', label: 'Voorbehoud', short: '?' },
  { value: 'niet_spelend', label: 'Niet spelend', short: 'NS' },
];

const STATUS_STYLE: Record<string, { bg: string; border: string; text: string }> = {
  aanwezig: { bg: '#dcfce7', border: '#16a34a', text: '#15803d' },
  afwezig: { bg: '#fde68a', border: '#d97706', text: '#92400e' },
  onder_voorbehoud: { bg: '#ede9fe', border: '#7c3aed', text: '#5b21b6' },
  niet_spelend: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
};

const TYPE_LABELS: Record<string, string> = {
  rehearsal: 'Repetitie',
  concert: 'Concert',
  uitstap: 'Uitstap',
};

const TYPE_DEFAULT_COLOR: Record<string, string> = {
  rehearsal: '#0f3460',
  concert: '#6d28d9',
  uitstap: '#0e7490',
};

const DAY_ABBR = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const DAY_FULL = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
const WEEKS_VISIBLE = 4;

function mondayOf(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  const dow = r.getDay();
  r.setDate(r.getDate() - (dow === 0 ? 6 : dow - 1));
  return r;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function isoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function eventColor(ev: MyEvent): string {
  return ev.color || TYPE_DEFAULT_COLOR[ev.type] || '#374151';
}

function dayName(date: string) {
  const d = new Date(date);
  return DAY_FULL[(d.getDay() + 6) % 7] ?? '';
}

function dateLabel(date: string) {
  return new Date(date).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function compactDateLabel(date: string) {
  return new Date(date).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
}

function timeRange(ev: MyEvent) {
  const fallbackEnd = endTimeFromDescription(ev.description, ev.startTime);
  const endTime = ev.endTime || fallbackEnd;
  return `${ev.startTime}${endTime ? ` - ${endTime}` : ''}`;
}

function endTimeFromDescription(description: string | null, startTime: string) {
  if (!description) return null;
  const times = [...description.matchAll(/(\d{1,2})[:.](\d{2})/g)]
    .map((match) => `${(match[1] ?? '').padStart(2, '0')}:${match[2] ?? ''}`);
  if (times.length < 2) return null;
  const startIndex = times.findIndex((time) => time === startTime);
  return startIndex >= 0 ? times[startIndex + 1] ?? null : times[1] ?? null;
}

function attendanceTone(status: string | null) {
  if (!status) return { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };
  if (status === 'aanwezig') return { bg: '#dcfce7', border: '#16a34a', text: '#14532d' };
  if (status === 'afwezig') return { bg: '#fde68a', border: '#d97706', text: '#78350f' };
  return STATUS_STYLE[status] ?? { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };
}

function groupMembers<T extends { instrument: string | null; groups: OrchestraGroup[] }>(members: T[]) {
  const grouped = new Map<string, T[]>();
  for (const member of members) {
    const key = member.instrument || member.groups.map((g) => g.name).join(', ') || 'Overig';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(member);
  }
  return grouped;
}


function applyOwnAttendanceDelta(event: MyEvent, status: string): MyEvent {
  const previous = event.myAttendance;
  const wasFilled = !!previous;
  const willBeFilled = !!status;
  const wasPresent = previous === 'aanwezig';
  const willBePresent = status === 'aanwezig';
  const presentDelta = Number(willBePresent) - Number(wasPresent);
  const filledDelta = Number(willBeFilled) - Number(wasFilled);

  return {
    ...event,
    myAttendance: status,
    attendanceLocked: false,
    participantTotal: Math.max(0, event.participantTotal + presentDelta),
    filledTotal: Math.max(0, event.filledTotal + filledDelta),
    groupCounts: event.groups.length === 1
      ? event.groupCounts.map((group) => group.groupId === event.groups[0]!.id
          ? { ...group, count: Math.max(0, group.count + presentDelta) }
          : group)
      : event.groupCounts,
  };
}

interface ConfirmationEvent {
  id: string;
  title: string;
  type: string;
  date: string;
  startTime: string;
  location: string | null;
  groups: { id: string; name: string }[];
  memberCount: number;
  filledCount: number;
  unfilledCount: number;
}

const NotificationBar = styled.div`
  background: #fffbeb;
  border-bottom: 2px solid #f59e0b;
  padding: 0.75rem 0;

  @media (max-width: 1024px) {
    margin-top: -0.5rem;
  }

  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const NotificationInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NotificationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.45rem 0.75rem;
  background: white;
  border: 1.5px solid #fcd34d;
  border-radius: 8px;
`;

const NotificationLabel = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  color: #92400e;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const NotificationMeta = styled.span`
  font-weight: 400;
  color: #78350f;
  font-size: 0.8rem;
`;

const NotificationBadge = styled.span<{ $urgent: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${({ $urgent }) => ($urgent ? '#fee2e2' : '#dcfce7')};
  color: ${({ $urgent }) => ($urgent ? '#b91c1c' : '#15803d')};
  border: 1px solid ${({ $urgent }) => ($urgent ? '#fca5a5' : '#86efac')};
  white-space: nowrap;
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 1.5rem 0 1.5rem;
  background: #f6f7f9;

  @media (max-width: 1024px) {
    padding-top: 0;
    padding-bottom: 3rem;
  }
`;

const DashboardContent = styled(Container)<{ $hasNotifications?: boolean }>`
  padding-top: ${({ $hasNotifications }) => ($hasNotifications ? '1.5rem' : '0')};
`;

const DashHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  h1 {
    color: #1a1a2e;
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.6rem, 3vw, 2.25rem);
    margin: 0;
  }

  p {
    color: #6b7280;
    font-size: 0.92rem;
    margin: 0.2rem 0 0;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 0.76fr) minmax(0, 1.44fr);
  grid-template-rows: minmax(0, 1fr);
  gap: 1rem;
  align-items: stretch;
  /* Fill the viewport between the site header and the footer so nothing is
     cut off on load and the page itself does not scroll on desktop. */
  height: calc(100vh - 11rem);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    align-items: start;
    height: auto;
  }
`;

const Panel = styled.section`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  overflow: hidden;
`;

const ListPanel = styled(Panel)`
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 980px) {
    height: auto;
    max-height: none;
  }
`;

const PanelHeader = styled.div`
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fbfbfc;
`;

const PanelTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.65rem;

  h2 {
    font-size: 0.92rem;
    color: #111827;
    margin: 0;
  }
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: #4b5563;
  background: #eef2f7;
  border-radius: 999px;
  padding: 0.18rem 0.5rem;
  font-size: 0.76rem;
  font-weight: 700;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.45rem 0.6rem;
  background: white;
  color: #6b7280;

  input {
    width: 100%;
    border: 0;
    outline: 0;
    font-size: 0.88rem;
    color: #111827;
    background: transparent;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: 0.45rem;
`;

const FilterSelect = styled.select`
  flex: 1;
  min-width: 0;
  padding: 0.38rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.8rem;
  background: white;
  color: #374151;
  cursor: pointer;
`;

const EventListScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0.75rem;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0.45rem 0 0.5rem;
`;

const EventCard = styled.div<{
  $color: string;
  $toneBg: string;
  $toneBorder: string;
  $selected: boolean;
  $cancelled: boolean;
  $urgent: boolean;
}>`
  width: 100%;
  display: grid;
  grid-template-columns: 6px minmax(0, 1fr);
  padding: 0;
  margin: 0 0 0.65rem;
  border: 1px solid ${({ $selected, $color, $toneBorder }) => ($selected ? $color : $toneBorder)};
  border-radius: 8px;
  background: ${({ $urgent, $toneBg }) => ($urgent ? '#fee2e2' : $toneBg)};
  opacity: ${({ $cancelled }) => ($cancelled ? 0.62 : 1)};
  overflow: hidden;
  text-align: left;
  cursor: pointer;
  box-shadow: ${({ $selected, $color }) => ($selected ? `0 0 0 2px ${$color}44` : 'none')};
`;

const AccentBar = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
`;

const EventCardBody = styled.div`
  padding: 0.75rem 0.8rem;
`;

const EventTopLine = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.65rem;
`;

const EventTitle = styled.h3`
  color: #111827;
  font-size: 0.95rem;
  line-height: 1.25;
  margin: 0;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.38rem;
  flex-wrap: wrap;
  color: #4b5563;
  font-size: 0.78rem;
  margin-top: 0.28rem;
`;

const Badge = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.42rem;
  border-radius: 4px;
  color: ${({ $color }) => $color ?? '#374151'};
  background: ${({ $color }) => ($color ? `${$color}18` : '#eef2f7')};
  border: 1px solid ${({ $color }) => ($color ? `${$color}44` : '#e5e7eb')};
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
`;

const StatusBadge = styled(Badge)<{ $bg: string; $text: string; $border: string }>`
  background: ${({ $bg }) => $bg};
  color: ${({ $text }) => $text};
  border-color: ${({ $border }) => $border};
`;

const GroupCounts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.28rem;
  margin-top: 0.5rem;
`;

const InlineActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.65rem;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  padding: 0.34rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #0f3460;
    color: #0f3460;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const IconButton = styled(ActionButton)`
  width: 2rem;
  height: 2rem;
  justify-content: center;
  padding: 0;
`;

const AttRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.32rem;
`;

const StatusBtn = styled.button<{ $active: boolean; $status: string }>`
  min-height: 2rem;
  padding: 0.28rem 0.58rem;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: ${({ $active }) => ($active ? 800 : 700)};
  border: 1.5px solid ${({ $active, $status }) => ($active ? (STATUS_STYLE[$status]?.border ?? '#d1d5db') : '#d1d5db')};
  background: ${({ $active, $status }) => ($active ? (STATUS_STYLE[$status]?.bg ?? '#f3f4f6') : 'white')};
  color: ${({ $active, $status }) => ($active ? (STATUS_STYLE[$status]?.text ?? '#374151') : '#4b5563')};
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: ${({ $status }) => STATUS_STYLE[$status]?.border ?? '#0f3460'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const SubPanel = styled.div`
  margin-top: 0.65rem;
  border-top: 1px solid rgba(17, 24, 39, 0.1);
  padding-top: 0.65rem;
`;

const AttendeeGroup = styled.div`
  margin-bottom: 0.55rem;
`;

const AttendeeSectionTitle = styled.div`
  color: #6b7280;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
`;

const NameChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const NameChip = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 999px;
  padding: 0.14rem 0.45rem;
  font-size: 0.74rem;
  font-weight: 700;
  background: ${({ $status }) => STATUS_STYLE[$status]?.bg ?? '#eef2f7'};
  color: ${({ $status }) => STATUS_STYLE[$status]?.text ?? '#374151'};
`;

const CalendarPanel = styled(Panel)`
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 980px) {
    height: auto;
    display: block;
  }
`;

const CalNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fbfbfc;
  flex-shrink: 0;
`;

const CalNavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;

  &:hover {
    border-color: #0f3460;
    color: #0f3460;
  }
`;

const CalNavLabel = styled.div`
  color: #111827;
  font-size: 0.95rem;
  font-weight: 800;
  text-transform: capitalize;
`;

const GridScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  max-width: 100%;

  @media (max-width: 980px) {
    flex: none;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 52px repeat(7, minmax(0, 1fr));
  grid-template-rows: auto;
  grid-auto-rows: minmax(0, 1fr);
  min-width: 0;
  height: 100%;

  @media (max-width: 980px) {
    height: auto;
    grid-auto-rows: auto;
  }

  @media (max-width: 760px) {
    grid-template-columns: 52px repeat(7, minmax(82px, 1fr));
    min-width: 614px;
  }
`;

const CornerCell = styled.div`
  background: #f8fafc;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
`;

const DayHeaderCell = styled.div<{ $today: boolean }>`
  padding: 0.55rem 0.4rem;
  text-align: center;
  background: ${({ $today }) => ($today ? '#eff6ff' : '#f8fafc')};
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid ${({ $today }) => ($today ? '#0f3460' : '#e5e7eb')};
`;


const DayHeaderLabel = styled.div`
  color: #6b7280;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-top: 0.15rem;
`;

const WeekLabelCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  color: #94a3b8;
  background: #f8fafc;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.6rem;
  font-weight: 800;
`;

const DayCell = styled.div<{ $today: boolean; $selected: boolean }>`
  min-height: 0;
  padding: 0.34rem;
  overflow: hidden;
  background: ${({ $selected, $today }) => ($selected ? '#eff6ff' : $today ? '#f8fbff' : 'white')};
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 980px) {
    min-height: 108px;
    overflow: visible;
  }
`;

const Chip = styled.button<{ $color: string; $bg: string; $selected: boolean; $cancelled: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 4px minmax(0, 1fr);
  padding: 0;
  margin-bottom: 0.28rem;
  border: 1px solid ${({ $selected, $color }) => ($selected ? $color : `${$color}66`)};
  border-radius: 6px;
  background: ${({ $bg }) => $bg};
  overflow: hidden;
  opacity: ${({ $cancelled }) => ($cancelled ? 0.6 : 1)};
  cursor: pointer;
  text-align: left;
`;

const ChipAccent = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
`;

const ChipBody = styled.span`
  min-width: 0;
  padding: 0.28rem 0.38rem;
`;

const ChipTitle = styled.span`
  display: block;
  color: #111827;
  font-size: 0.7rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #4b5563;
  font-size: 0.62rem;
  font-weight: 700;
  margin-top: 0.08rem;
`;

const DetailPanel = styled.div<{ $color: string }>`
  border-top: 3px solid ${({ $color }) => $color};
  background: #fbfbfc;
  padding: 1rem;
  flex-shrink: 0;

  /* The attendee roster can be long; keep the core detail visible and let the
     roster scroll within a bounded, viewport-relative height so everyone is
     reachable without the whole calendar panel scrolling. */
  ${SubPanel} {
    max-height: clamp(160px, 26vh, 320px);
    overflow-y: auto;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const DetailTitle = styled.h3`
  color: #111827;
  font-size: 1.05rem;
  line-height: 1.25;
  margin: 0;
`;

const DetailMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  color: #4b5563;
  font-size: 0.8rem;
  margin-top: 0.35rem;
`;

const DetailStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin: 0.9rem 0;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  padding: 0.55rem 0.7rem;
`;

const StatNumber = styled.div`
  color: #111827;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 0.2rem;
`;

const EmptyState = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  color: #6b7280;
`;

function useAttendanceHandler(
  event: MyEvent,
  onAttendanceChange: (id: string, status: string) => void,
  onLockDiscovered: (id: string) => void,
) {
  const [saving, setSaving] = useState(false);

  const handleStatus = async (status: string) => {
    if (event.status === 'cancelled' || saving || event.attendanceLocked || !event.canSetAttendance) return;
    setSaving(true);
    try {
      await client.post('/attendance/my-attendance', { eventId: event.id, status });
      onAttendanceChange(event.id, status);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 403) onLockDiscovered(event.id);
    } finally {
      setSaving(false);
    }
  };

  return { saving, handleStatus };
}

const AttendeeView = ({ eventId, flat }: { eventId: string; flat?: boolean }) => {
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/attendance/events/${eventId}/attendees`)
      .then((r) => setAttendees(r.data))
      .catch(() => setAttendees([]))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>Laden...</p>;
  if (!attendees.length) return <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>Nog niemand heeft ingevuld.</p>;

  if (flat) {
    return (
      <SubPanel>
        <NameChips>
          {attendees.map((attendee) => (
            <NameChip key={attendee.userId} $status={attendee.status}>
              {attendee.firstName} {attendee.lastName}
            </NameChip>
          ))}
        </NameChips>
      </SubPanel>
    );
  }

  return (
    <SubPanel>
      {[...groupMembers(attendees).entries()].map(([section, list]) => (
        <AttendeeGroup key={section}>
          <AttendeeSectionTitle>{section}</AttendeeSectionTitle>
          <NameChips>
            {list.map((attendee) => (
              <NameChip key={attendee.userId} $status={attendee.status}>
                {attendee.firstName} {attendee.lastName}
              </NameChip>
            ))}
          </NameChips>
        </AttendeeGroup>
      ))}
    </SubPanel>
  );
};

const AttendanceButtons = ({
  event,
  onAttendanceChange,
  onLockDiscovered,
}: {
  event: MyEvent;
  onAttendanceChange: (id: string, status: string) => void;
  onLockDiscovered: (id: string) => void;
}) => {
  const { saving, handleStatus } = useAttendanceHandler(event, onAttendanceChange, onLockDiscovered);

  if (event.status === 'cancelled') return null;
  if (!event.canSetAttendance) {
    return (
      <AttRow>
        <Badge title="Je bent geen lid van de groep(en) voor dit evenement">
          <Users size={12} /> Niet van toepassing
        </Badge>
      </AttRow>
    );
  }

  return (
    <AttRow>
      {STATUS_OPTIONS.map((opt) => (
        <StatusBtn
          key={opt.value}
          $active={event.myAttendance === opt.value}
          $status={opt.value}
          onClick={(e) => {
            e.stopPropagation();
            handleStatus(opt.value);
          }}
          disabled={saving || event.attendanceLocked}
          title={opt.label}
        >
          {opt.short}
        </StatusBtn>
      ))}
      {event.attendanceLocked && (
        <Badge title="Ingevuld door bestuur">
          <Lock size={12} /> Vast
        </Badge>
      )}
    </AttRow>
  );
};

const EventListItem = ({
  event,
  selected,
  isAdmin,
  onSelect,
  onAttendanceChange,
  onLockDiscovered,
  onTogglePin,
}: {
  event: MyEvent;
  selected: boolean;
  isAdmin: boolean;
  onSelect: (event: MyEvent) => void;
  onAttendanceChange: (id: string, status: string) => void;
  onLockDiscovered: (id: string) => void;
  onTogglePin: (event: MyEvent) => void;
}) => {
  const [showAttendees, setShowAttendees] = useState(false);
  const colour = eventColor(event);
  const tone = attendanceTone(event.myAttendance);
  const missingPinned = event.isPinned && !event.myAttendance && event.canSetAttendance;
  const statusLabel = !event.canSetAttendance
    ? 'Niet van toepassing'
    : event.myAttendance
      ? STATUS_OPTIONS.find((opt) => opt.value === event.myAttendance)?.label ?? event.myAttendance
      : 'Niet ingevuld';

  return (
    <EventCard
      $color={colour}
      $toneBg={tone.bg}
      $toneBorder={tone.border}
      $selected={selected}
      $cancelled={event.status === 'cancelled'}
      $urgent={missingPinned}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(event)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(event);
        }
      }}
    >
      <AccentBar $color={colour} />
      <EventCardBody>
        <EventTopLine>
          <div>
            <EventTitle>
              {event.isPinned && <Pin size={13} fill={colour} color={colour} style={{ marginRight: 5, verticalAlign: '-2px' }} />}
              {missingPinned && <AlertTriangle size={14} color="#b91c1c" style={{ marginRight: 5, verticalAlign: '-2px' }} />}
              {event.title}
            </EventTitle>
            <MetaLine>
              <Clock size={13} />
              <span>{dayName(event.date)} {compactDateLabel(event.date)} · {timeRange(event)}</span>
            </MetaLine>
            {event.location && (
              <MetaLine>
                <MapPin size={13} />
                <span>{event.location}</span>
              </MetaLine>
            )}
          </div>
          <StatusBadge $bg={tone.bg} $text={tone.text} $border={tone.border}>{statusLabel}</StatusBadge>
        </EventTopLine>

        <MetaLine>
          <Badge $color={colour}>{TYPE_LABELS[event.type] ?? event.type}</Badge>
          {event.groups.length ? event.groups.map((group) => <Badge key={group.id}>{group.name}</Badge>) : <Badge>Alle groepen</Badge>}
          {event.status === 'cancelled' && <Badge $color="#b91c1c">Geannuleerd</Badge>}
        </MetaLine>

        <GroupCounts>
          <Badge>
            <Users size={12} /> {event.participantTotal} aanwezig / {event.filledTotal} ingevuld
          </Badge>
        </GroupCounts>

        <InlineActions onClick={(e) => e.stopPropagation()}>
          <AttendanceButtons event={event} onAttendanceChange={onAttendanceChange} onLockDiscovered={onLockDiscovered} />
          <ActionButton onClick={() => setShowAttendees((v) => !v)}>
            {showAttendees ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            Ingevuld
          </ActionButton>
          {isAdmin && (
            <IconButton onClick={() => onTogglePin(event)} title={event.isPinned ? 'Niet meer pinnen' : 'Pin bovenaan'}>
              <Pin size={14} fill={event.isPinned ? colour : 'none'} color={colour} />
            </IconButton>
          )}
        </InlineActions>

        {showAttendees && <AttendeeView eventId={event.id} flat={event.groups.some((g) => g.name === 'Trommelkorps')} />}
      </EventCardBody>
    </EventCard>
  );
};

const CalendarDetailPanel = ({
  event,
  isAdmin,
  onAttendanceChange,
  onLockDiscovered,
  onClose,
  onTogglePin,
}: {
  event: MyEvent;
  isAdmin: boolean;
  onAttendanceChange: (id: string, status: string) => void;
  onLockDiscovered: (id: string) => void;
  onClose: () => void;
  onTogglePin: (event: MyEvent) => void;
}) => {
  const [showAttendees, setShowAttendees] = useState(false);
  const colour = eventColor(event);
  const tone = attendanceTone(event.myAttendance);

  return (
    <DetailPanel $color={colour}>
      <DetailHeader>
        <div>
          <DetailTitle>{event.title}</DetailTitle>
          <DetailMeta>
            <span>{dayName(event.date)} {dateLabel(event.date)}</span>
            <span>{timeRange(event)}</span>
            {event.location && <span>{event.location}</span>}
            <Badge $color={colour}>{TYPE_LABELS[event.type] ?? event.type}</Badge>
            {event.isPinned && <Badge $color={colour}><Pin size={12} fill={colour} /> Gepind</Badge>}
          </DetailMeta>
        </div>
        <IconButton onClick={onClose} title="Sluiten"><X size={14} /></IconButton>
      </DetailHeader>

      <DetailStats>
        <StatBox>
          <StatNumber>{event.participantTotal}</StatNumber>
          <StatLabel>Aanwezig</StatLabel>
        </StatBox>
        <StatBox>
          <StatNumber>{event.filledTotal}</StatNumber>
          <StatLabel>Ingevuld</StatLabel>
        </StatBox>
        <StatBox>
          <StatNumber>{event.groups.length || event.groupCounts.length}</StatNumber>
          <StatLabel>Groepen</StatLabel>
        </StatBox>
      </DetailStats>

      <GroupCounts>
        {event.groups.length ? event.groups.map((group) => <Badge key={group.id}>{group.name}</Badge>) : <Badge>Alle groepen</Badge>}
      </GroupCounts>

      <InlineActions>
        <StatusBadge $bg={tone.bg} $text={tone.text} $border={tone.border}>
          {!event.canSetAttendance ? 'Niet van toepassing' : event.myAttendance ? STATUS_OPTIONS.find((opt) => opt.value === event.myAttendance)?.label : 'Niet ingevuld'}
        </StatusBadge>
        <AttendanceButtons event={event} onAttendanceChange={onAttendanceChange} onLockDiscovered={onLockDiscovered} />
        <ActionButton onClick={() => setShowAttendees((v) => !v)}>
          {showAttendees ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          Ingevulde leden
        </ActionButton>
        {isAdmin && (
          <IconButton onClick={() => onTogglePin(event)} title={event.isPinned ? 'Niet meer pinnen' : 'Pin bovenaan'}>
            <Pin size={14} fill={event.isPinned ? colour : 'none'} color={colour} />
          </IconButton>
        )}
      </InlineActions>

      {showAttendees && <AttendeeView eventId={event.id} flat={event.groups.some((g) => g.name === 'Trommelkorps')} />}
    </DetailPanel>
  );
};

const CalendarGrid = ({
  events,
  selectedId,
  isAdmin,
  weekOffset,
  onWeekOffsetChange,
  onSelect,
  onAttendanceChange,
  onLockDiscovered,
  onTogglePin,
}: {
  events: MyEvent[];
  selectedId: string | null;
  isAdmin: boolean;
  weekOffset: number;
  onWeekOffsetChange: (n: number) => void;
  onSelect: (event: MyEvent) => void;
  onAttendanceChange: (id: string, status: string) => void;
  onLockDiscovered: (id: string) => void;
  onTogglePin: (event: MyEvent) => void;
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const base = mondayOf(today);
  base.setDate(base.getDate() + weekOffset * 7);
  const weeks = Array.from({ length: WEEKS_VISIBLE }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => addDays(base, w * 7 + d)));
  const allDays = weeks.flat();
  const visStart = allDays[0]!;
  const visEnd = allDays[allDays.length - 1]!;

  const byDay = new Map<string, MyEvent[]>();
  for (const ev of events) {
    const key = ev.date.slice(0, 10);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key)!.push(ev);
  }

  const selectedEvent = events.find((event) => event.id === selectedId) ?? null;
  const rangeLabel = visStart.toLocaleDateString('nl-BE', { month: 'long' }) === visEnd.toLocaleDateString('nl-BE', { month: 'long' })
    ? visStart.toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' })
    : `${visStart.toLocaleDateString('nl-BE', { month: 'short' })} - ${visEnd.toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' })}`;

  return (
    <CalendarPanel>
      <CalNav>
        <CalNavBtn onClick={() => onWeekOffsetChange(weekOffset - WEEKS_VISIBLE)}><ChevronLeft size={16} /></CalNavBtn>
        <CalNavLabel>{rangeLabel}</CalNavLabel>
        <CalNavBtn onClick={() => onWeekOffsetChange(weekOffset + WEEKS_VISIBLE)}><ChevronRight size={16} /></CalNavBtn>
      </CalNav>
      <GridScroll>
        <Grid>
          <CornerCell />
          {(weeks[0] ?? []).map((day, index) => {
            const isToday = sameDay(day, today);
            return (
              <DayHeaderCell key={isoDate(day)} $today={isToday}>
                <DayHeaderLabel>{DAY_ABBR[index]}</DayHeaderLabel>
              </DayHeaderCell>
            );
          })}
          {weeks.map((week) => (
            <Fragment key={isoDate(week[0]!)}>
              <WeekLabelCell>week van {compactDateLabel(isoDate(week[0]!))}</WeekLabelCell>
              {week.map((day) => {
                const key = isoDate(day);
                const dayEvents = byDay.get(key) ?? [];
                const hasSelected = dayEvents.some((event) => event.id === selectedId);
                return (
                  <DayCell key={key} $today={sameDay(day, today)} $selected={hasSelected}>
                    {dayEvents.map((event) => {
                      const colour = eventColor(event);
                      const tone = attendanceTone(event.myAttendance);
                      return (
                        <Chip
                          key={event.id}
                          $color={colour}
                          $bg={event.isPinned && !event.myAttendance ? '#fee2e2' : tone.bg}
                          $selected={event.id === selectedId}
                          $cancelled={event.status === 'cancelled'}
                          onClick={() => onSelect(event)}
                        >
                          <ChipAccent $color={colour} />
                          <ChipBody>
                            <ChipTitle>
                              {event.isPinned && <Pin size={10} fill={colour} color={colour} style={{ marginRight: 3, verticalAlign: '-1px' }} />}
                              {event.title}
                            </ChipTitle>
                            <ChipMeta>
                              <span>{timeRange(event)}</span>
                              <span><Users size={10} /> {event.participantTotal}</span>
                            </ChipMeta>
                          </ChipBody>
                        </Chip>
                      );
                    })}
                  </DayCell>
                );
              })}
            </Fragment>
          ))}
        </Grid>
      </GridScroll>

      {selectedEvent && (
        <CalendarDetailPanel
          event={selectedEvent}
          isAdmin={isAdmin}
          onAttendanceChange={onAttendanceChange}
          onLockDiscovered={onLockDiscovered}
          onClose={() => onSelect(selectedEvent)}
          onTogglePin={onTogglePin}
        />
      )}
    </CalendarPanel>
  );
};

export const MemberDashboard = () => {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAttendance, setFilterAttendance] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [confirmationEvents, setConfirmationEvents] = useState<ConfirmationEvent[]>([]);
  const [attendanceDetailId, setAttendanceDetailId] = useState<string | null>(null);

  const isAdmin = !!user?.isAdmin;

  const fetchEvents = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await client.get('/attendance/my-events');
      const nextEvents: MyEvent[] = res.data.map((event: MyEvent) => ({
        ...event,
        isPinned: !!event.isPinned,
        canSetAttendance: event.canSetAttendance ?? true,
        participantTotal: event.participantTotal ?? 0,
        filledTotal: event.filledTotal ?? 0,
        groupCounts: event.groupCounts ?? [],
        groupCountsImported: !!event.groupCountsImported,
      }));
      setEvents(nextEvents);
      // Keep a still-valid selection across refreshes, but never auto-select on load.
      setSelectedId((current) => current && nextEvents.some((event) => event.id === current) ? current : null);
    } catch {
      if (showLoading) setEvents([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    if (!isAdmin) return;
    client.get('/attendance/admin/needs-confirmation')
      .then((r) => setConfirmationEvents(r.data))
      .catch(() => {});
  }, [isAdmin]);

  const availableGroups = useMemo(() => {
    const seen = new Map<string, string>();
    for (const ev of events) {
      for (const g of ev.groups) {
        if (!seen.has(g.id)) seen.set(g.id, g.name);
      }
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((event) => {
      if (filterGroup && !event.groups.some((g) => g.id === filterGroup)) return false;
      if (filterType && event.type !== filterType) return false;
      if (filterAttendance === 'niet_ingevuld') {
        if (!event.canSetAttendance || event.myAttendance) return false;
      } else if (filterAttendance && event.myAttendance !== filterAttendance) return false;
      if (q && ![
        event.title,
        TYPE_LABELS[event.type] ?? event.type,
        event.location ?? '',
        ...event.groups.map((g) => g.name),
      ].join(' ').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [events, query, filterGroup, filterType, filterAttendance]);

  const pinnedEvents = filteredEvents.filter((event) => event.isPinned);
  const regularEvents = filteredEvents.filter((event) => !event.isPinned);
  const hasAttendanceConfirmations = isAdmin && confirmationEvents.length > 0;
  const handleSelect = (event: MyEvent) => {
    const targetDate = new Date(event.date);
    const todayMonday = mondayOf(new Date());
    const eventMonday = mondayOf(targetDate);
    const diffDays = Math.round((eventMonday.getTime() - todayMonday.getTime()) / 86400000);
    setWeekOffset(Math.floor(diffDays / 7));
    setSelectedId((current) => current === event.id ? null : event.id);
  };

  const handleAttendanceChange = (eventId: string, status: string) => {
    setEvents((prev) => prev.map((event) => event.id === eventId ? applyOwnAttendanceDelta(event, status) : event));
    fetchEvents(false);
  };

  const handleLockDiscovered = (eventId: string) => {
    setEvents((prev) => prev.map((event) => event.id === eventId ? { ...event, attendanceLocked: true } : event));
  };

  const handleTogglePin = async (event: MyEvent) => {
    if (!isAdmin) return;
    const next = !event.isPinned;
    setEvents((prev) => prev.map((item) => item.id === event.id ? { ...item, isPinned: next } : item));
    try {
      await client.patch(`/events/${event.id}`, { isPinned: next });
    } catch {
      setEvents((prev) => prev.map((item) => item.id === event.id ? { ...item, isPinned: event.isPinned } : item));
    }
  };

  if (attendanceDetailId) {
    return (
      <PageWrapper>
        <Container>
          <AttendanceDetail
            eventId={attendanceDetailId}
            onBack={() => setAttendanceDetailId(null)}
          />
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      {hasAttendanceConfirmations && (
        <NotificationBar>
          <Container>
            <NotificationInner>
              {confirmationEvents.map((ev) => {
                const dateStr = new Date(ev.date).toLocaleDateString('nl-BE', { weekday: 'short', day: 'numeric', month: 'short' });
                const allFilled = ev.unfilledCount === 0;
                return (
                  <NotificationRow key={ev.id}>
                    <NotificationLabel>
                      <ClipboardCheck size={16} color="#d97706" style={{ flexShrink: 0 }} />
                      <span>{ev.title}</span>
                      <NotificationMeta>· {dateStr} {ev.startTime}{ev.location ? ` · ${ev.location}` : ''}</NotificationMeta>
                    </NotificationLabel>
                    <NotificationBadge $urgent={!allFilled}>
                      {allFilled
                        ? `✓ ${ev.filledCount}/${ev.memberCount} ingevuld`
                        : `${ev.unfilledCount} niet ingevuld`}
                    </NotificationBadge>
                    <Button size="sm" onClick={() => setAttendanceDetailId(ev.id)}>
                      Bevestig aanwezigheid
                    </Button>
                  </NotificationRow>
                );
              })}
            </NotificationInner>
          </Container>
        </NotificationBar>
      )}
      <DashboardContent $hasNotifications={hasAttendanceConfirmations}>
        <DashHeader>
          <TitleBlock>
            <h1>Hallo {user?.firstName}</h1>
            <p>Bekijk evenementen, vul aanwezigheid in en volg de kalender in een oogopslag.</p>
          </TitleBlock>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {isAdmin && (
              <Link to="/admin">
                <Button size="sm">
                  <Shield size={14} style={{ marginRight: '0.4rem' }} />
                  Bestuursportaal
                </Button>
              </Link>
            )}
            <Button size="sm" variant="ghost" onClick={logout}>Uitloggen</Button>
          </div>
        </DashHeader>

        {loading ? (
          <EmptyState>Laden...</EmptyState>
        ) : events.length === 0 ? (
          <EmptyState>Geen aankomende evenementen voor jouw groepen.</EmptyState>
        ) : (
          <DashboardGrid>
            <ListPanel>
              <PanelHeader>
                <PanelTitle>
                  <h2>Aankomende evenementen</h2>
                  <CountPill><CalendarDays size={13} /> {filteredEvents.length}</CountPill>
                </PanelTitle>
                <SearchBox>
                  <Search size={15} />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Zoek op titel, groep of locatie" />
                </SearchBox>
                <FilterRow>
                  {availableGroups.length > 1 && (
                    <FilterSelect value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                      <option value="">Alle groepen</option>
                      {availableGroups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </FilterSelect>
                  )}
                  <FilterSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="">Alle types</option>
                    <option value="rehearsal">Repetitie</option>
                    <option value="concert">Concert</option>
                    <option value="uitstap">Uitstap</option>
                  </FilterSelect>
                  <FilterSelect value={filterAttendance} onChange={(e) => setFilterAttendance(e.target.value)}>
                    <option value="">Alle statussen</option>
                    <option value="niet_ingevuld">Niet ingevuld</option>
                    <option value="aanwezig">Aanwezig</option>
                    <option value="afwezig">Afwezig</option>
                    <option value="onder_voorbehoud">Voorbehoud</option>
                    <option value="niet_spelend">Niet spelend</option>
                  </FilterSelect>
                </FilterRow>
              </PanelHeader>
              <EventListScroll>
                {pinnedEvents.length > 0 && (
                  <>
                    <SectionLabel><Pin size={13} /> Belangrijk</SectionLabel>
                    {pinnedEvents.map((event) => (
                      <EventListItem
                        key={event.id}
                        event={event}
                        selected={event.id === selectedId}
                        isAdmin={isAdmin}
                        onSelect={handleSelect}
                        onAttendanceChange={handleAttendanceChange}
                        onLockDiscovered={handleLockDiscovered}
                        onTogglePin={handleTogglePin}
                      />
                    ))}
                  </>
                )}
                <SectionLabel><Clock size={13} /> Chronologisch</SectionLabel>
                {regularEvents.map((event) => (
                  <EventListItem
                    key={event.id}
                    event={event}
                    selected={event.id === selectedId}
                    isAdmin={isAdmin}
                    onSelect={handleSelect}
                    onAttendanceChange={handleAttendanceChange}
                    onLockDiscovered={handleLockDiscovered}
                    onTogglePin={handleTogglePin}
                  />
                ))}
                {filteredEvents.length === 0 && (
                  <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0.5rem' }}>Geen evenementen gevonden.</p>
                )}
              </EventListScroll>
            </ListPanel>

            <CalendarGrid
              events={events}
              selectedId={selectedId}
              isAdmin={isAdmin}
              weekOffset={weekOffset}
              onWeekOffsetChange={setWeekOffset}
              onSelect={handleSelect}
              onAttendanceChange={handleAttendanceChange}
              onLockDiscovered={handleLockDiscovered}
              onTogglePin={handleTogglePin}
            />
          </DashboardGrid>
        )}
      </DashboardContent>
    </PageWrapper>
  );
};
